import { onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  disabledPlatformFeatureFallback,
  disabledPlatformFeatureMessage,
  matchedPlatformFeatureKey,
} from "@/helpers/platformFeatures";
import { useErrorStore } from "@/stores/ErrorStore";
import { usePlatformFeatureStore } from "@/stores/PlatformFeatureStore";
import { useUserStore } from "@/stores/UserStore";

const POLL_INTERVAL_MS = 30_000;

export function usePlatformFeatureEnforcement() {
  const route = useRoute();
  const router = useRouter();
  const userStore = useUserStore();
  const errorStore = useErrorStore();
  const platformFeatureStore = usePlatformFeatureStore();

  const leaveIfCurrentFeatureDisabled = () => {
    const featureKey = matchedPlatformFeatureKey(route);
    if (!featureKey || !platformFeatureStore.isDisabled(featureKey)) return;
    errorStore.showError(
      disabledPlatformFeatureMessage(
        platformFeatureStore.nameFor(featureKey),
        featureKey,
      ),
      false,
    );
    router.replace(disabledPlatformFeatureFallback(route.name)).catch(() => {});
  };

  const refreshStatus = async () => {
    if (!userStore.currentUser) return;
    try {
      await platformFeatureStore.loadStatus();
    } catch {
      return;
    }
    leaveIfCurrentFeatureDisabled();
  };

  watch(
    () => userStore.currentUser,
    (user) => {
      if (user) refreshStatus();
    },
  );

  let timer: ReturnType<typeof setInterval> | undefined;
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      refreshStatus();
    }
  };

  onMounted(() => {
    refreshStatus();
    timer = setInterval(refreshStatus, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
  });

  onUnmounted(() => {
    if (timer) clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  });
}
